import { useCallback, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import useDismiss from "./useDismiss";
import menuStyles from "./FilterDropdown.module.css";
import styles from "./InlineSelect.module.css";

// Borderless value + chevron that opens a single-select list; used in detail cards.
// `renderValue` lets callers show something richer than the label (e.g. a Tag).
export default function InlineSelect({ label, options, value, onChange, renderValue }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(open, close, rootRef);

  const selected = options.find((option) => option.value === value);

  return (
    <div className={`${menuStyles.root} ${styles.root}`} ref={rootRef}>
      <button
        type="button"
        className={`${styles.trigger} d-flex align-items-center`}
        onClick={() => setOpen((isOpen) => !isOpen)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${label}: ${selected?.label ?? "None"}`}
      >
        {renderValue ? renderValue(selected) : selected?.label}
        <ChevronDown size={16} strokeWidth={1.75} className={open ? menuStyles.chevronOpen : ""} aria-hidden="true" />
      </button>

      {open ? (
        <ul className={`${menuStyles.menu} ${styles.menu}`} role="listbox" aria-label={label}>
          {options.map((option) => {
            const isSelected = option.value === value;

            return (
              <li key={option.value}>
                <button
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={`${menuStyles.option} d-flex align-items-center justify-content-between`}
                  onClick={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                >
                  {option.label}
                  {isSelected ? <Check size={14} strokeWidth={2} aria-hidden="true" /> : null}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
